import SubstrateSearchResponse, {
    QueryAlterationType,
} from '../../../data/schema/SubstrateSearchResponse';
import SuperExecuteSearchResponseMessage from '../../../data/schema/SuperExecuteSearchResponseMessage';
import ExecuteSearchResponseMessage from 'owa-service/lib/contract/ExecuteSearchResponseMessage';
import { isFeatureEnabled } from 'owa-feature-flags';

export default async function augmentExecuteSearchResponseMessage(
    substrateSearchResponse: Response,
    substrateSearchResponseBody: SubstrateSearchResponse,
    executeSearchResponseMessage: ExecuteSearchResponseMessage
): Promise<SuperExecuteSearchResponseMessage> {
    // If failure response (or no response), return ES body.
    if (!substrateSearchResponse || substrateSearchResponse.status !== 200) {
        return {
            ...executeSearchResponseMessage,
        };
    }

    const queryAlterationResponse =
        substrateSearchResponseBody.EntitySets &&
        substrateSearchResponseBody.EntitySets[0] &&
        substrateSearchResponseBody.EntitySets[0].QueryAlterationResponse;

    // If there's no QueryAlterationResponse from 3S response, return ES body.
    if (!queryAlterationResponse) {
        return {
            ...executeSearchResponseMessage,
        };
    }

    /**
     * If QueryAlterationType isn't supported just extend the ES response with
     * empty data.
     */
    if (!isSupportedQueryAlterationType(queryAlterationResponse.QueryAlterationType)) {
        return {
            ...executeSearchResponseMessage,
            querySuggestionData: {
                alteredQuery: '',
                flaggedTokens: null,
                recourseQuery: '',
                suggestedSearchTerm: null,
                suggestedSearchTermReferenceId: null,
                queryAlterationType: null,
            },
        };
    }

    /**
     * If we get here, the QueryAlterationType is "Suggestion", which is the
     * response for spell-corrected search.
     */
    const superExecuteSearchResponseMessage: SuperExecuteSearchResponseMessage = {
        ...executeSearchResponseMessage,
        querySuggestionData: {
            alteredQuery:
                queryAlterationResponse.AlteredQuery &&
                queryAlterationResponse.AlteredQuery.AlteredQuery &&
                queryAlterationResponse.AlteredQuery.AlteredQuery.RawString,
            flaggedTokens:
                queryAlterationResponse.AlteredQuery &&
                queryAlterationResponse.AlteredQuery.FlaggedTokens,
            recourseQuery:
                queryAlterationResponse.RecourseQuery &&
                queryAlterationResponse.RecourseQuery.RawString,
            suggestedSearchTerm:
                queryAlterationResponse.AlteredQuery &&
                queryAlterationResponse.AlteredQuery.FlaggedTokens &&
                queryAlterationResponse.AlteredQuery.FlaggedTokens[0] &&
                queryAlterationResponse.AlteredQuery.FlaggedTokens[0].Suggestion,
            suggestedSearchTermReferenceId:
                queryAlterationResponse.AlteredQuery &&
                queryAlterationResponse.AlteredQuery.AlteredQuery &&
                queryAlterationResponse.AlteredQuery.AlteredQuery.ReferenceId,
            queryAlterationType: queryAlterationResponse.QueryAlterationType,
        },
    };

    return superExecuteSearchResponseMessage;
}

const isSupportedQueryAlterationType = (queryAlterationType: QueryAlterationType): boolean => {
    const supportedQueryAlterationTypes = ['Suggestion'];

    if (isFeatureEnabled('sea-spellingCorrectedSearchNoResults')) {
        supportedQueryAlterationTypes.push('NoResultModification');
    }

    if (isFeatureEnabled('sea-naturalLanguage')) {
        supportedQueryAlterationTypes.push('NoRequeryModification');
    }

    if (isFeatureEnabled('sea-modificationQueryAlteration')) {
        supportedQueryAlterationTypes.push('Modification');
    }

    if (isFeatureEnabled('sea-extensionQueryAlteration')) {
        supportedQueryAlterationTypes.push('Extension');
    }

    return supportedQueryAlterationTypes.includes(queryAlterationType);
};
